/**
 * What Lou keeps in the browser.
 *
 * Almost nothing, deliberately. Conversations and their transcripts live on
 * the server: a browser-local copy is a second source of truth, and it was
 * what made the same conversation look different on different machines.
 *
 * What is left is per-browser convenience that nobody would want synced —
 * which conversation this browser had open, and anything typed but not yet
 * sent. Losing either costs the user a click.
 */

const LAST_CONVERSATION_KEY = "lou:last_conversation";
const DRAFT_KEY_PREFIX = "lou:draft:";

/** Keys written by builds before conversations moved to the server. */
const RETIRED_KEYS = [
  "harris_ai_assistant_session",
  "harris_ai_assistant_tab_instance",
  "ai_assistant_conversations",
  "messages",
  "sessionInitialized",
  "historyMetadata",
];

export const draftKey = (conversationId) => `${DRAFT_KEY_PREFIX}${conversationId}`;

const safely = (fn) => {
  try {
    return fn();
  } catch {
    // Private browsing, or storage disabled. None of this is load-bearing.
    return undefined;
  }
};

export const readDraft = (conversationId) =>
  safely(() => window.localStorage.getItem(draftKey(conversationId))) ?? "";

export const writeDraft = (conversationId, text) =>
  safely(() => {
    if (text) {
      window.localStorage.setItem(draftKey(conversationId), text);
    } else {
      window.localStorage.removeItem(draftKey(conversationId));
    }
  });

/**
 * Clears everything Lou has put in this browser, on sign-out.
 *
 * Also removes the keys older builds wrote. A stale transcript in
 * localStorage is worse than no transcript: it would render as though it were
 * current, for a user who may not even be the one who left it there.
 */
export const clearAssistantStore = async () =>
  safely(() => {
    window.localStorage.removeItem(LAST_CONVERSATION_KEY);

    const drafts = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(DRAFT_KEY_PREFIX)) drafts.push(key);
    }
    drafts.forEach((key) => window.localStorage.removeItem(key));

    RETIRED_KEYS.forEach((key) => {
      window.localStorage.removeItem(key);
      window.sessionStorage.removeItem(key);
    });
  });
